import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function useResolvedMeals(program) {
  const [byId, setById] = useState({});
  const key = (program?.weeks || []).flatMap(w => (w.days || []).flatMap(d => (d.meals || []).map(m => m.meal_id))).join(',');
  useEffect(() => {
    const ids = key ? key.split(',') : [];
    if (!ids.length) { setById({}); return; }
    api.post('/meals/resolve', { ids }).then(r => setById(Object.fromEntries((r.data || []).map(m => [m.id, m])))).catch(() => setById({}));
  }, [key]);
  return byId;
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-lg bg-[var(--c-film)] px-3 py-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-mono font-semibold text-sm">{value}</div>
    </div>
  );
}

export function ProgramSummary({ program }) {
  const t = program.daily_target || {};
  const v = program.validation || {};
  return (
    <div className="space-y-2" data-testid="nut-review-summary">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Stat label="Daily target" value={`${t.calories} kcal`} testid="nut-review-target-kcal" />
        <Stat label="Sedentary baseline" value={program.maintenance_kcal ? `${program.maintenance_kcal} kcal` : 'not from profile'} testid="nut-review-maintenance" />
        <Stat label="Protein target" value={`${t.protein || 0} g`} testid="nut-review-target-protein" />
        <Stat label="Tolerance" value={`±${v.tolerance_kcal || 50} kcal`} testid="nut-review-tolerance" />
      </div>
      {(program.excluded_meals || []).length > 0 && (
        <div className="text-xs rounded-lg p-2.5 bg-[#FFB800]/10 text-[#FFB800]" data-testid="nut-review-excluded">
          Excluded from generation (ingredient not found in the Food Library): {program.excluded_meals.map(e => `${e.name} (${e.unmatched_ingredients.join(', ')})`).join('; ')}
        </div>
      )}
      {v.issues?.length > 0 && !v.fully_validated && (
        <ul className="text-xs list-disc pl-5 text-muted-foreground" data-testid="nut-review-issues">{v.issues.map((s, i) => <li key={i}>{s}</li>)}</ul>
      )}
      {v.passed ? (
        <div className="flex items-center gap-2 text-xs rounded-lg p-2.5 bg-[var(--c-green-soft)] text-[var(--c-green-ink)]" data-testid="nut-review-passed">
          <CheckCircle2 className="w-4 h-4" /> All {v.days_checked} days are within ±{v.tolerance_kcal} kcal of the target.
          {v.protein_short_days > 0 && ` ${v.protein_short_days} day(s) below 95% of the protein target.`}
        </div>
      ) : (
        <div className="flex items-start gap-2 text-xs rounded-lg p-2.5 bg-red-500/10 text-red-400" data-testid="nut-review-failed">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{v.failed_days} of {v.days_checked} days are outside ±{v.tolerance_kcal} kcal. Assignment is blocked until the flagged days are resolved in the Nutrition Programs builder.
            {v.protein_short_days > 0 && ` ${v.protein_short_days} day(s) below 95% of the protein target.`}</span>
        </div>
      )}
    </div>
  );
}

function DayCard({ day, wi, byId }) {
  const ok = day.within_tolerance;
  const tot = day.totals || {};
  return (
    <div className={`rounded-xl border p-3 ${ok ? 'border-[var(--c-hair)]' : 'border-red-500/40'}`} data-testid={`nut-review-day-${wi}-${day.day_index}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-display font-bold text-sm">{DAYS[day.day_index]}</div>
        <div className="font-mono text-xs" data-testid={`nut-review-day-total-${wi}-${day.day_index}`}>
          {tot.calories} / {day.target_kcal} kcal
          <span className={ok ? 'text-[var(--c-green-ink)]' : 'text-red-400'}> ({day.deviation_kcal > 0 ? '+' : ''}{day.deviation_kcal})</span>
          <span className={day.protein_warning ? 'text-[#FFB800]' : 'text-muted-foreground'}> · P {tot.protein_g}/{day.target_protein} g</span>
        </div>
      </div>
      <div className="mt-2 space-y-1.5">
        {(day.meals || []).map((m, i) => {
          const meal = byId[m.meal_id] || {};
          return (
            <div key={i} className="text-xs" data-testid={`nut-review-meal-${wi}-${day.day_index}-${i}`}>
              <div className="flex justify-between gap-2">
                <span><span className="text-muted-foreground">{m.slot} · </span><span className="font-semibold">{meal.name || '…'}</span></span>
                <span className="font-mono text-muted-foreground shrink-0">{meal.calories ?? '–'} kcal · {meal.protein_g ?? '–'} g P</span>
              </div>
              {meal.ingredients && <div className="text-[11px] text-muted-foreground/80 mt-0.5">{meal.ingredients}</div>}
            </div>
          );
        })}
      </div>
      {!ok && day.flag_reason && <div className="mt-2 text-[11px] text-red-400" data-testid={`nut-review-flag-${wi}-${day.day_index}`}>{day.flag_reason}</div>}
    </div>
  );
}

export default function NutritionProgramReview({ program, weekIndex = 0 }) {
  const byId = useResolvedMeals(program);
  const week = program.weeks?.[weekIndex];
  return (
    <div className="space-y-3" data-testid="nut-program-review">
      <ProgramSummary program={program} />
      <div className="space-y-2">
        {(week?.days || []).map(d => <DayCard key={d.day_index} day={d} wi={weekIndex} byId={byId} />)}
      </div>
    </div>
  );
}
